import React, { useEffect, useState, useContext, useCallback } from 'react';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import { Button } from 'react-bootstrap';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import ModalPop from 'components/sales/modal/modal';
import Images from 'assets/images/imageData';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'services/interceptor/Interceptor';
import Config from 'services/config/config';
import {
  getVendorSubmissionTitle,
  getVendorApprovalSubmissionMessage,
  isApprovedVendorStatus,
} from '../vendorSubmissionMessages';
import {
  validateVendorAddressFieldLengths,
  VENDOR_ADDRESS_MAX_LENGTH,
  validateFieldMaxLength,
} from '../../vendorOnboarding/validation';
import './vendorAddressForm.css';

const DEFAULT_COUNTRY = 'India';

const getValidationErrorMessage = (data, fallback = 'Failed to save vendor address') => {
  if (!data) return fallback;
  if (data.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return data.message || fallback;
};

const INITIAL_FORM = {
  name: '',
  address: '',
  address2: '',
  country: DEFAULT_COUNTRY,
  geo_state_id: '',
  geo_city_id: '',
  geo_pincode_id: '',
  contact: '',
  gst_no: '',
  arn_no: '',
  phone_no: '',
  email: '',
  fax_no: '',
  home_page: '',
};

const mapDropdownOptions = (items = [], labelKeys = ['name']) =>
  (items || []).map((item) => {
    const label =
      labelKeys.map((key) => item[key]).find((val) => val != null && val !== '') ||
      item.pincode ||
      String(item.id);
    return { id: item.id, name: String(label) };
  });

const findDropdownLabel = (options, id) =>
  options.find((item) => String(item.id) === String(id))?.name ?? '';

const VendorAddressForm = ({
  getVendorAddressFields,
  getGeoCitiesByState,
  getGeoPincodesByCity,
  getVendorOnboardingList,
  vendorOnboardedList,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const { vendorId, addressId } = useParams();
  const history = useHistory();
  const isEdit = Boolean(addressId);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [geoStates, setGeoStates] = useState([]);
  const [geoCities, setGeoCities] = useState([]);
  const [geoPincodes, setGeoPincodes] = useState([]);
  const [isApprovedVendor, setIsApprovedVendor] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [gstMandatory, setGstMandatory] = useState(true);

  const detailUrl = `${baseUrl}/vendor_onboard_details/${vendorId}`;

  const getAddressFieldError = (key, value) => {
    if (key === 'address') {
      return validateFieldMaxLength(value, 'Address Line 1', VENDOR_ADDRESS_MAX_LENGTH);
    }
    if (key === 'address2') {
      return validateFieldMaxLength(value, 'Address Line 2', VENDOR_ADDRESS_MAX_LENGTH);
    }
    return '';
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: getAddressFieldError(key, value) }));
  };

  const loadCities = useCallback(
    async (geoStateId) => {
      if (!geoStateId) {
        setGeoCities([]);
        setGeoPincodes([]);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getGeoCitiesByState(geoStateId);
        if (result?.success) {
          setGeoCities(mapDropdownOptions(result.data, ['name', 'city_name']));
        } else {
          setGeoCities([]);
          toast.error(result?.message || 'Failed to load cities');
        }
        setGeoPincodes([]);
      } finally {
        setIsLoading(false);
      }
    },
    [getGeoCitiesByState]
  );

  const loadPincodes = useCallback(
    async (geoCityId) => {
      if (!geoCityId) {
        setGeoPincodes([]);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getGeoPincodesByCity(geoCityId);
        if (result?.success) {
          setGeoPincodes(mapDropdownOptions(result.data, ['pincode', 'name']));
        } else {
          setGeoPincodes([]);
          toast.error(result?.message || 'Failed to load pincodes');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getGeoPincodesByCity]
  );

  const applyAddressToForm = useCallback(
    async (address) => {
      if (!address) return;
      setFormData({
        name: address.name ?? '',
        address: address.address ?? '',
        address2: address.address2 ?? '',
        country: address.country || DEFAULT_COUNTRY,
        geo_state_id: address.geo_state_id ?? '',
        geo_city_id: address.geo_city_id ?? '',
        geo_pincode_id: address.geo_pincode_id ?? '',
        contact: address.contact ?? '',
        gst_no: address.gst_no ?? '',
        arn_no: address.arn_no ?? '',
        phone_no: address.phone_no ?? '',
        email: address.email ?? '',
        fax_no: address.fax_no ?? '',
        home_page: address.home_page ?? '',
      });
      setFieldErrors(validateVendorAddressFieldLengths({
        address: address.address ?? '',
        address2: address.address2 ?? '',
      }));
      if (address.geo_state_id) {
        await loadCities(address.geo_state_id);
      }
      if (address.geo_city_id) {
        await loadPincodes(address.geo_city_id);
      }
    },
    [loadCities, loadPincodes]
  );

  const loadFieldOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getVendorAddressFields(isEdit ? addressId : null, vendorId);

      if (!result?.success) {
        toast.error(result?.message || 'Failed to load address fields');
        return;
      }

      const data = result.data || {};
      setGeoStates(mapDropdownOptions(data.geo_states, ['name', 'state_name']));
      setGstMandatory(data.gst_mandatory !== false);

      const selectedAddress = data.selected_address || data.address;
      if (isEdit && selectedAddress) {
        await applyAddressToForm(selectedAddress);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        country: DEFAULT_COUNTRY,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [
    getVendorAddressFields,
    addressId,
    vendorId,
    isEdit,
    applyAddressToForm,
  ]);

  useEffect(() => {
    loadFieldOptions();
  }, [loadFieldOptions]);

  useEffect(() => {
    if (getVendorOnboardingList) {
      (async () => {
        await getVendorOnboardingList(null, 1, false, vendorId);
      })();
    }
  }, [getVendorOnboardingList, vendorId]);

  useEffect(() => {
    const vendors = vendorOnboardedList?.data || [];
    if (!vendors.length) return;

    // Redux may still hold a previous list page — only trust this vendorId.
    const vendor = vendors.find((item) => String(item.id) === String(vendorId));
    if (!vendor) return;

    const approved = isApprovedVendorStatus(vendor.approval_status);
    setIsApprovedVendor(approved);

    if (!isEdit && !approved) {
      toast.info('Add address is available only after the vendor is approved.');
      history.replace(detailUrl);
    }
  }, [vendorOnboardedList, isEdit, vendorId, history, detailUrl]);

  const handleStateChange = async (val) => {
    const stateId = val?.id ?? '';
    setFormData((prev) => ({
      ...prev,
      geo_state_id: stateId,
      geo_city_id: '',
      geo_pincode_id: '',
    }));
    await loadCities(stateId);
  };

  const handleCityChange = async (val) => {
    const cityId = val?.id ?? '';
    setFormData((prev) => ({
      ...prev,
      geo_city_id: cityId,
      geo_pincode_id: '',
    }));
    await loadPincodes(cityId);
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      toast.error('Address name is required');
      return;
    }
    if (!formData.address?.trim()) {
      toast.error('Address line 1 is required');
      return;
    }
    if (!formData.geo_state_id) {
      toast.error('State is required');
      return;
    }
    if (!formData.geo_city_id) {
      toast.error('City is required');
      return;
    }
    if (!formData.geo_pincode_id) {
      toast.error('Pincode is required');
      return;
    }
    if (gstMandatory && !formData.gst_no?.trim()) {
      toast.error('GST No is required');
      return;
    }

    const lengthErrors = validateVendorAddressFieldLengths(formData);
    if (Object.keys(lengthErrors).length) {
      setFieldErrors((prev) => ({ ...prev, ...lengthErrors }));
      toast.error('Please fix validation errors before submitting');
      return;
    }

    const fallback = isEdit
      ? 'Failed to update vendor address'
      : 'Failed to add vendor address';

    setIsSubmitting(true);
    try {
      const payload = {
        vendor_id: Number(vendorId),
        name: formData.name.trim(),
        address: formData.address.trim(),
        address2: formData.address2?.trim() || undefined,
        country: DEFAULT_COUNTRY,
        geo_state_id: formData.geo_state_id,
        geo_city_id: formData.geo_city_id,
        geo_pincode_id: formData.geo_pincode_id,
        contact: formData.contact?.trim() || undefined,
        gst_no: formData.gst_no?.trim() || undefined,
        arn_no: formData.arn_no?.trim() || undefined,
        phone_no: formData.phone_no?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        fax_no: formData.fax_no?.trim() || undefined,
        home_page: formData.home_page?.trim() || undefined,
      };

      const url = isEdit
        ? Config.preSalesList.editVendorAddress
        : Config.preSalesList.addVendorAddress;
      const submitPayload = isEdit ? { ...payload, id: Number(addressId) } : payload;

      const res = await axios.post(url, submitPayload);

      if (res?.data?.status === 0) {
        setIsModal(true);
        return;
      }

      toast.error(getValidationErrorMessage(res?.data, fallback));
    } catch (error) {
      toast.error(
        getValidationErrorMessage(error?.response?.data, error?.message || fallback)
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalHide = () => {
    setIsModal(false);
    history.push(detailUrl);
  };

  return (
    <Layout
      className="vendor-address-form-page"
      title={isEdit ? 'Edit Address' : 'Add Address'}
      isLoading={isLoading || isSubmitting}
      isGoback
      linkFlag
      linkUrl={detailUrl}
      useNewHeaderLayout
    >
      <div className="card vendor-address-form-card">
        <div className="card-header">Vendor Address</div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <ConstantInput
                label="Address Name"
                type="text"
                value={formData.name}
                handleChange={(val) => updateField('name', val)}
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Address Line 1"
                type="text"
                value={formData.address}
                handleChange={(val) => updateField('address', val)}
                maxLength={VENDOR_ADDRESS_MAX_LENGTH}
                errorMsg={fieldErrors.address}
                blockError={Boolean(fieldErrors.address)}
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Address Line 2"
                type="text"
                value={formData.address2}
                handleChange={(val) => updateField('address2', val)}
                maxLength={VENDOR_ADDRESS_MAX_LENGTH}
                errorMsg={fieldErrors.address2}
                blockError={Boolean(fieldErrors.address2)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Country"
                type="text"
                value={formData.country}
                disabled
                required
                isStarRequired
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.geoState}
                data={geoStates}
                value={findDropdownLabel(geoStates, formData.geo_state_id)}
                required
                isStarRequired
                getIdValue={handleStateChange}
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.geoCity}
                data={geoCities}
                value={findDropdownLabel(geoCities, formData.geo_city_id)}
                disabled={!formData.geo_state_id}
                required
                isStarRequired
                getIdValue={handleCityChange}
              />
            </div>
            <div className="col-md-4">
              <DropDownInput
                label={localization.pinCode}
                data={geoPincodes}
                value={findDropdownLabel(geoPincodes, formData.geo_pincode_id)}
                disabled={!formData.geo_city_id}
                required
                isStarRequired
                getIdValue={(val) => updateField('geo_pincode_id', val?.id ?? '')}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Contact"
                type="text"
                value={formData.contact}
                handleChange={(val) => updateField('contact', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Phone No"
                type="text"
                value={formData.phone_no}
                handleChange={(val) => updateField('phone_no', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label={localization.emailIdNo}
                type="text"
                value={formData.email}
                handleChange={(val) => updateField('email', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="GST No"
                type="text"
                value={formData.gst_no}
                handleChange={(val) =>
                  updateField('gst_no', String(val ?? '').toUpperCase())
                }
                required={gstMandatory}
                isStarRequired={gstMandatory}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="ARN No"
                type="text"
                value={formData.arn_no}
                handleChange={(val) => updateField('arn_no', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Fax No"
                type="text"
                value={formData.fax_no}
                handleChange={(val) => updateField('fax_no', val)}
              />
            </div>
            <div className="col-md-4">
              <ConstantInput
                label="Home Page"
                type="text"
                value={formData.home_page}
                handleChange={(val) => updateField('home_page', val)}
              />
            </div>
            <div className="col-md-12 vendor-address-form-actions">
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isEdit ? localization.update : localization.submitDtl}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ModalPop
        title={getVendorSubmissionTitle(isApprovedVendor)}
        show={isModal}
        handleModalHide={handleModalHide}
        keyboard
      >
        <div className="text-center">
          <div className="success">
            <img src={Images?.tick} alt="" />
          </div>
          <h5>
            {getVendorApprovalSubmissionMessage(
              isEdit ? 'edit_address' : 'add_address',
              isApprovedVendor
            )}
          </h5>
        </div>
      </ModalPop>
    </Layout>
  );
};

export default VendorAddressForm;
